import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Plus, MapPin, Clock, Edit2, Trash2, X, Repeat, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { calendarAPI } from '../services/api';
import {
  format, parseISO, startOfMonth, endOfMonth, addMonths, subMonths,
  startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, isSameDay, isToday
} from 'date-fns';
import RecurringEventModal from './RecurringEventModal';
import RecurringDeleteDialog from './RecurringDeleteDialog';

const TeamCalendar = ({ onEventChange }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [isExpanded, setIsExpanded] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingRecurringEvent, setEditingRecurringEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    event_type: 'training',
    start_datetime: '',
    end_datetime: '',
    location: '',
    description: ''
  });

  const { hasPermission } = useTeam();
  const isCoach = hasPermission('manage_calendar');

  useEffect(() => {
    loadEvents();
  }, [currentWeek, user?.team_id]);

  const loadEvents = async () => {
    if (!user?.team_id) return;

    try {
      setLoading(true);
      // Load events for the month containing current week
      const monthStart = startOfMonth(currentWeek);
      const monthEnd = endOfMonth(currentWeek);
      const response = await calendarAPI.getEvents(user.team_id, monthStart.toISOString(), monthEnd.toISOString());
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to load calendar events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for week view
  const getWeekDays = (date) => {
    const start = startOfWeek(date, { weekStartsOn: 1 }); // Monday
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  };

  // Mobile: Show 7 days starting from today
  const getMobileDays = () => {
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => addDays(today, i));
  };

  const getWeekEvents = (date) => {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(date, { weekStartsOn: 1 });

    return events.filter(event => {
      const eventDate = parseISO(event.start_datetime);
      return eventDate >= weekStart && eventDate <= weekEnd;
    });
  };

  const getEventsForDay = (day) => {
    return events.filter(event => isSameDay(parseISO(event.start_datetime), day));
  };

  const isPastEvent = (event) => {
    if (!event) return false;
    const start = parseISO(event.start_datetime);
    return start < new Date();
  };

  const handleCreateEvent = () => {
    setEditingEvent(null);
    setFormData({
      title: '',
      event_type: 'training',
      start_datetime: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      end_datetime: '',
      location: '',
      description: ''
    });
    setShowEventModal(true);
  };

  const handleEditEvent = (event) => {
    if (isPastEvent(event)) {
      alert("Geçmiş idmanlar değiştirilemez.");
      return;
    }
    setEditingEvent(event);
    setFormData({
      title: event.title,
      event_type: event.event_type,
      start_datetime: format(parseISO(event.start_datetime), "yyyy-MM-dd'T'HH:mm"),
      end_datetime: event.end_datetime ? format(parseISO(event.end_datetime), "yyyy-MM-dd'T'HH:mm") : '',
      location: event.location || '',
      description: event.description || ''
    });
    setShowEventModal(true);
  };

  const handleCreateRecurringEvent = () => {
    setEditingRecurringEvent(null);
    setShowRecurringModal(true);
  };

  const handleEditRecurringEvent = async (event) => {
    setEditingRecurringEvent(event);
    setShowRecurringModal(true);
  };

  const handleDeleteEvent = async (eventId, isRecurring, parentEventId, event) => {
    if (isPastEvent(event)) {
      alert("Geçmiş idmanlar silinemez.");
      return;
    }

    if (isRecurring) {
      // For recurring events, show the choice dialog
      setDeletingEvent({ id: eventId, isRecurring: true, parentEventId });
      setShowDeleteDialog(true);
      return;
    }

    // For non-recurring, show simple confirmation via dialog (re-using the same component)
    setDeletingEvent({ id: eventId, isRecurring: false });
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async (type) => {
    try {
      const { id, isRecurring, parentEventId } = deletingEvent;

      if (type === 'instance') {
        // Delete only this instance
        // "id" for recurring events in get_calendar_events is "rec-{id}-{date}"
        // We need to extract the date
        const parts = id.split('-');
        // Format: rec-UUID-YYYY-MM-DD
        // The UUID might contain dashes, so we need to be careful
        // The prefix is 'rec-' (4 chars)
        // The date is the last 3 parts joined by '-' (10 chars)
        const dateStr = id.slice(-10);
        await calendarAPI.deleteRecurringEventInstance(parentEventId, dateStr);
      } else if (type === 'series') {
        // Delete entire series
        await calendarAPI.deleteRecurringEvent(parentEventId);
      } else {
        // Single non-recurring event
        await calendarAPI.deleteEvent(id);
      }

      setShowDeleteDialog(false);
      await loadEvents();
      if (onEventChange) onEventChange();
    } catch (error) {
      console.error('Failed to delete event:', error);
      alert(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        ...formData,
        start_datetime: new Date(formData.start_datetime).toISOString(),
        end_datetime: formData.end_datetime ? new Date(formData.end_datetime).toISOString() : null
      };

      if (editingEvent) {
        await calendarAPI.updateEvent(editingEvent.id, data);
      } else {
        await calendarAPI.createEvent(user.team_id, data);
      }

      setShowEventModal(false);
      await loadEvents();
      if (onEventChange) onEventChange();
    } catch (error) {
      console.error('Failed to save event:', error);
      alert(error.response?.data?.detail || 'Failed to save event');
    }
  };

  const handleSubmitRecurring = async (data) => {
    try {
      if (editingRecurringEvent && editingRecurringEvent.parent_event_id) {
        await calendarAPI.updateRecurringEvent(editingRecurringEvent.parent_event_id, data);
      } else {
        await calendarAPI.createRecurringEvent(user.team_id, data);
      }

      setShowRecurringModal(false);
      await loadEvents();
      if (onEventChange) onEventChange();
    } catch (error) {
      console.error('Failed to save recurring event:', error);
      alert(error.response?.data?.detail || 'Failed to save recurring event');
    }
  };

  const eventTypeColors = {
    training: 'bg-blue-600',
    match: 'bg-red-600',
    announcement: 'bg-yellow-600'
  };

  const eventTypeLabels = {
    training: 'İdman',
    match: 'Maç',
    announcement: 'Duyuru'
  };

  const weekDays = getWeekDays(currentWeek);
  const dayNames = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

  return (
    <Card className="bg-gradient-to-br from-[#0a0a0b] via-[#111113] to-[#0a0a0b] border-gray-800/50 shadow-xl" data-testid="team-calendar">
      <CardHeader className="pb-3 border-b border-gray-800/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-blue-500/20 blur-xl rounded-full"></div>
              <div className="relative bg-gradient-to-br from-blue-600 to-blue-800 p-2.5 rounded-xl shadow-lg">
                <CalendarIcon className="w-5 h-5 text-white" />
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">PROGRAM</div>
              <div className="text-white font-bold text-lg leading-tight">
                {format(startOfWeek(currentWeek, { weekStartsOn: 1 }), 'd MMM')} - {format(endOfWeek(currentWeek, { weekStartsOn: 1 }), 'd MMM')}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}
              className="h-8 w-8 p-0 hover:bg-white/5 text-gray-400 hover:text-white border border-gray-800/50 hover:border-gray-700 transition-all"
            >
              ←
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
              className="h-8 w-8 p-0 hover:bg-white/5 text-gray-400 hover:text-white border border-gray-800/50 hover:border-gray-700 transition-all"
            >
              →
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-3">
        {loading ? (
          <div className="text-center py-12 text-gray-400">Yükleniyor...</div>
        ) : (
          <>
            {/* Mobile: Horizontal Week View (Today First) */}
            <div className="lg:hidden flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent">
              {getMobileDays().map((day) => {
                const dayEvents = getEventsForDay(day);
                const today = isToday(day);
                const dayOfWeek = day.getDay(); // 0 = Sunday, 1 = Monday, etc.
                const dayNameIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to our array index

                return (
                  <div
                    key={day.toString()}
                    className={`flex-shrink-0 w-32 rounded-xl overflow-hidden transition-all duration-300 ${today
                      ? 'bg-gradient-to-br from-blue-600/20 via-blue-700/10 to-transparent border-2 border-blue-500/50 shadow-lg shadow-blue-500/10'
                      : 'bg-[#141416]/50 border border-gray-800/50 hover:border-gray-700/50'
                      }`}
                  >
                    {/* Day Header */}
                    <div className={`p-2.5 text-center ${today ? 'bg-blue-600/10' : 'bg-gray-900/30'}`}>
                      <div className={`text-[9px] uppercase tracking-wider font-bold ${today ? 'text-blue-400' : 'text-gray-500'}`}>
                        {dayNames[dayNameIndex]}
                      </div>
                      <div className={`text-2xl font-bold leading-none mt-0.5 ${today ? 'text-white' : 'text-gray-300'}`}>
                        {format(day, 'd')}
                      </div>
                    </div>

                    {/* Events */}
                    <div className="p-1.5 space-y-1 min-h-[80px]">
                      {dayEvents.length === 0 ? (
                        <div className="text-center py-4 text-gray-600 text-[10px]">—</div>
                      ) : (
                        dayEvents.map(event => (
                          <div
                            key={event.id}
                            className={`${eventTypeColors[event.event_type]}/20 backdrop-blur-sm border ${eventTypeColors[event.event_type]}/30 rounded-lg p-1.5 cursor-pointer hover:scale-105 transition-transform group relative ${isPastEvent(event) ? 'opacity-50' : ''}`}
                            onClick={() => {
                              const isEditable = !event.announcement_id && !event.id.toString().startsWith('te-');
                              if (isEditable) handleEditEvent(event);
                            }}
                          >
                            <div className="flex items-start justify-between gap-1">
                              <div className="flex-1 min-w-0">
                                {event.event_type !== 'announcement' && (
                                  <div className="flex items-center gap-1 mb-0.5">
                                    <Clock className="w-2.5 h-2.5 flex-shrink-0 text-white/70" />
                                    <span className="text-[9px] font-bold text-white/90">
                                      {format(parseISO(event.start_datetime), 'HH:mm')}
                                    </span>
                                  </div>
                                )}
                                <div className="text-[10px] font-semibold text-white leading-tight line-clamp-2">
                                  {event.title}
                                </div>
                              </div>
                              {/* Attendance Dot */}
                              {event.user_attendance_status && (event.event_type === 'training' || event.event_type === 'match') && (
                                <div className={`w-1.5 h-1.5 rounded-full absolute bottom-1 right-1 shadow-sm ${event.user_attendance_status === 'attending' ? 'bg-green-500 shadow-green-500/50' : 'bg-red-500 shadow-red-500/50'
                                  }`} />
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop: Grid Week View (Monday First) */}
            <div className="hidden lg:grid grid-cols-7 gap-2 mb-4">
              {weekDays.map((day, idx) => (
                <div key={day.toString()} className="min-h-[120px]">
                  <div className={`text-center p-2 rounded-t-lg ${isToday(day) ? 'bg-blue-600' : 'bg-gray-800'}`}>
                    <div className="text-xs text-gray-400">{dayNames[idx]}</div>
                    <div className={`text-lg font-bold ${isToday(day) ? 'text-white' : 'text-gray-200'}`}>
                      {format(day, 'd')}
                    </div>
                  </div>
                  <div className="bg-[#0a0a0b] rounded-b-lg p-2 min-h-[80px] space-y-1">
                    {getEventsForDay(day).map(event => (
                      <div
                        key={event.id}
                        className={`${eventTypeColors[event.event_type]} rounded p-2 text-xs cursor-pointer hover:opacity-80 transition-opacity relative ${isPastEvent(event) ? 'opacity-40 grayscale-[0.5]' : ''}`}
                        onClick={() => {
                          const isEditable = !event.announcement_id && !event.id.toString().startsWith('te-');
                          if (isEditable) handleEditEvent(event);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            {event.is_recurring && <Repeat className="w-3 h-3" />}
                            {event.event_type !== 'announcement' && (
                              <>
                                <Clock className="w-3 h-3" />
                                <span>{format(parseISO(event.start_datetime), 'HH:mm')}</span>
                              </>
                            )}
                          </div>
                          {/* Attendance Dot for Desktop Grid */}
                          {event.user_attendance_status && (event.event_type === 'training' || event.event_type === 'match') && (
                            <div className={`w-2 h-2 rounded-full shadow-sm ${event.user_attendance_status === 'attending' ? 'bg-green-500 shadow-green-500/50' : 'bg-red-500 shadow-red-500/50'
                              }`} />
                          )}
                        </div>
                        <div className="font-semibold mt-1 truncate">{event.title}</div>
                        {event.location && (
                          <div className="flex items-center gap-1 text-gray-200 mt-1">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate">{event.location}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* View All Button */}
            <div className="flex justify-center mt-3 pt-3 border-t border-gray-800/30">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-gray-400 hover:text-white hover:bg-white/5 text-xs gap-2"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-3.5 h-3.5" />
                    Haftalık Görünüm
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3.5 h-3.5" />
                    Tüm Etkinlikleri Göster
                  </>
                )}
              </Button>
            </div>

            {/* Expanded Month View */}
            {isExpanded && (
              <div className="mt-6 pt-6 border-t border-gray-800">
                <div className="space-y-2">
                  {events.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      Bu ayda etkinlik yok
                    </div>
                  ) : (
                    events
                      .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))
                      .map(event => (
                        <div
                          key={event.id}
                          className={`bg-[#0a0a0b] border border-gray-800 rounded-lg p-4 hover:border-gray-700 transition-colors relative overflow-hidden ${isPastEvent(event) ? 'opacity-60' : ''}`}
                        >
                          {/* Attendance Status Bar/Indicator for List View */}
                          {event.user_attendance_status && (event.event_type === 'training' || event.event_type === 'match') && (
                            <div className={`absolute left-0 top-0 bottom-0 w-1 ${event.user_attendance_status === 'attending' ? 'bg-green-500' : 'bg-red-500'
                              }`} />
                          )}
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={`${eventTypeColors[event.event_type]} text-white text-xs`}>
                                  {eventTypeLabels[event.event_type]}
                                </Badge>
                                {event.is_recurring && (
                                  <Repeat className="w-4 h-4 text-blue-400" title="Tekrarlayan idman" />
                                )}
                                <span className="text-white font-medium">{event.title}</span>
                                {isPastEvent(event) && (
                                  <span className="text-[10px] text-gray-500 font-normal ml-auto">(Geçmiş)</span>
                                )}
                              </div>
                              <div className="flex items-center gap-4 text-sm text-gray-400">
                                <div className="flex items-center gap-1">
                                  <CalendarIcon className="w-4 h-4" />
                                  {format(parseISO(event.start_datetime), 'dd MMM yyyy')}
                                </div>
                                {event.event_type !== 'announcement' && (
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {format(parseISO(event.start_datetime), 'HH:mm')}
                                  </div>
                                )}
                                {event.location && (
                                  <div className="flex items-center gap-1">
                                    <MapPin className="w-4 h-4" />
                                    {event.location}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Single Event Modal */}
        <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
          <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white">
            <DialogHeader>
              <DialogTitle>{editingEvent ? 'Etkinliği Düzenle' : 'Yeni Etkinlik'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Başlık</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="bg-[#0a0a0b] border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Tip</label>
                <Select
                  value={formData.event_type}
                  onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                >
                  <SelectTrigger className="bg-[#0a0a0b] border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1b] border-gray-700">
                    <SelectItem value="training">İdman</SelectItem>
                    <SelectItem value="match">Maç</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Başlangıç</label>
                  <Input
                    type="datetime-local"
                    value={formData.start_datetime}
                    onChange={(e) => setFormData({ ...formData, start_datetime: e.target.value })}
                    required
                    className="bg-[#0a0a0b] border-gray-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Bitiş (İsteğe bağlı)</label>
                  <Input
                    type="datetime-local"
                    value={formData.end_datetime}
                    onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
                    className="bg-[#0a0a0b] border-gray-700 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Konum</label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="bg-[#0a0a0b] border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Açıklama</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-[#0a0a0b] border-gray-700 text-white"
                />
              </div>
              <DialogFooter className="flex justify-between sm:justify-between items-center w-full">
                {editingEvent && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      handleDeleteEvent(editingEvent.id, editingEvent.is_recurring, editingEvent.parent_event_id, editingEvent);
                      setShowEventModal(false);
                    }}
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10 px-2"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    İptal Et / Sil
                  </Button>
                )}
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowEventModal(false)} className="border-gray-700">
                    Kapat
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    {editingEvent ? 'Güncelle' : 'Oluştur'}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Recurring Event Modal */}
        <RecurringEventModal
          open={showRecurringModal}
          onOpenChange={setShowRecurringModal}
          onSubmit={handleSubmitRecurring}
          editingEvent={editingRecurringEvent}
        />

        {/* Delete Confirmation Dialog */}
        <RecurringDeleteDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          onConfirm={handleConfirmDelete}
          isRecurring={deletingEvent?.isRecurring}
        />
      </CardContent>
    </Card >
  );
};

export default TeamCalendar;
